import { ArrowBack } from "@mui/icons-material";
import InputIcon from "@mui/icons-material/Input";
import { SortingState } from "@tanstack/react-table";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import { product } from "Common/data";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Container, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchWarehouseDropdown } from "slices/Dropdowns/Warehouse/WarehouseDropdown/thunk";
import { fetchStockOverviewDetail } from "slices/Inventory/Instock/StockOverview/thunk";

type ProductStockInfoProps = {
  warehouseId?: string;
};

const ProductStockInfo: React.FC<ProductStockInfoProps> = ({ warehouseId: propWarehouseId }) => {
  const { id, warehouseId: paramWarehouseId } = useParams();
  document.title = "Inventory";

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Selector for stock overview detail
  const selectStockOverviewDetail = createSelector(
    (state: any) => state.inventoryStockOverviewSlice,
    (item) => ({
      detail: item.currentStockOverviewDetail,
      loading: item.loading,
      error: item.error,
      detailRowCount: item.detailRowCount
    })
  );

  const { detail, loading, error, detailRowCount } = useSelector(selectStockOverviewDetail);

  const { selectedWarehouse } = useSelector(
    createSelector(
      (state: any) => state.warehouseDropdownReducer,
      (data) => ({
        selectedWarehouse: data.selected,
      })
    )
  );

  // Priority: URL param > prop > Redux selected warehouse
  const effectiveWarehouseId = paramWarehouseId || propWarehouseId || selectedWarehouse?.value;



  // Fetch stock overview detail when id and warehouseId are available
  useEffect(() => {
    if (id && effectiveWarehouseId) {
      dispatch(
        fetchStockOverviewDetail({
          id,
          warehouseId: effectiveWarehouseId,
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortDirection,
          search,
          filters: filterValue,
        })
      );
    }
  }, [
    dispatch,
    id,
    effectiveWarehouseId,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue
  ]); // Added all d

  // Debouncing for search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchWarehouseDropdown());
  }, [dispatch]);

  // Extract data from detail response
  const data = useMemo(() => {
    if (!detail?.data) return [];
    // Adjust this based on your API response structure
    return Array.isArray(detail.data) ? detail.data : [detail.data];
  }, [detail]);



  // const canUpdate = item_inward_permissions?.permissions?.Update === true;
  const canUpdate = true;

  const DynamicAction = canUpdate
    ? [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const menuItems = [
            {
              icon: InputIcon,
              label: "Inward",
              permission: canUpdate,
              link: (item: any) =>
                `/warehouse-item-inward-create-grn/${data.po_id}/${effectiveWarehouseId}`,
              color: "primary",
            },
          ];
          return <DynamicMenuItem row={data} menuItems={menuItems} />;
        },
      },
    ]
    : [];

  const columns = [
    {
      header: "Sr No.",
      accessorKey: "sr_no",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => row.original.sr_no || "-",
    },
    {
      header: "Product Name",
      accessorKey: "product_name",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: any }) => row.original.item_name || "-",
    },
    {
      header: "Warranty Date",
      accessorKey: "warranty_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }: { row: any }) => (
        <FormattedDate date={row.original.warranty_date} />
      ),
    },
    {
      header: "Unit",
      accessorKey: "unit",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => row.original.unit || "-",
    },
    {
      header: "Serial No",
      accessorKey: "serial_no",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: any }) => row.original.serial_no || "-",
    },

    {
      header: "Status",
      accessorKey: "packing_status",
      enableSorting: false,
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl: API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.DetailedFilterDropdown("packing_status", id || "", effectiveWarehouseId),
      cell: ({ row }: { row: any }) => {
        const status = row.original.packing_status;
        return <StatusChip status={status} />;

        const getStatusColor = (status: string) => {
          switch (status?.toLowerCase()) {
            case "packed":
              return "#FFB2B2";
            case "received":
              return "#B7FAEB";
            case "dispatched":
              return "#be83bb";
            default:
              return "#ccc";
          }
        };
        const displayText = status
          ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
          : "-";

        return status ? (
          <StatusBadge
            status={displayText}
            bgColor={getStatusColor(status)}
            color="#000000ff"
          />
        ) : (
          "-"
        );
      },
    },



    {
      header: "QC Status",
      accessorKey: "status",
      enableSorting: false,
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl: API_ENDPOINT?.INVENTORY?.IN_STOCK?.STOCKOVERVIEW?.DetailedFilterDropdown("status", id || "", effectiveWarehouseId),
      cell: ({ row }: { row: any }) => {
        const status = row.original.status;
        return <StatusChip status={status} />;

        const getStatusColor = (status: string) => {
          switch (status?.toLowerCase()) {
            case "damaged":
              return "#FFB2B2";
            case "robust":
              return "#B7FAEB";
            default:
              return "#ccc";
          }
        };
        const displayText = status
          ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
          : "-";

        return status ? (
          <StatusBadge
            status={displayText}
            bgColor={getStatusColor(status)}
            color="#000000ff"
          />
        ) : (
          "-"
        );
      },
    },

    {
      header: "Inward Date",
      accessorKey: "inward_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }: { row: any }) => (
        <FormattedDate date={row.original.inward_date} />
      ),
    },
    // {
    //   header: "QC",
    //   accessorKey: "qc",
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   cell: ({ row }: { row: any }) => {
    //     const qc = row.original.qc;

    //     const getQCColor = (qc: string) => {
    //       switch (qc?.toUpperCase()) {
    //         case "PASS":
    //           return "#B7FAEB";
    //         case "FAIL":
    //           return "#FFB2B2";
    //         default:
    //           return "#ccc";
    //       }
    //     };

    //     return qc ? (
    //       <StatusBadge
    //         status={qc.toUpperCase()}
    //         bgColor={getQCColor(qc)}
    //         color="#000000ff"
    //       />
    //     ) : (
    //       "-"
    //     );
    //   },
    // },
  ];

  return (
    <Fragment>
      <div className="page-content ">
        <Container fluid className="px-3">
          <Card>
            <Card.Header>
              <div
                className="d-flex align-items-center p-2"
                style={{ gap: "10px", cursor: "pointer" }}
                onClick={() => navigate(-1)}
              >
                <ArrowBack
                  className="me-2"
                  style={{ fontSize: "1.25rem", cursor: "pointer" }}
                />
                <p className="mb-0 fw-bold" style={{ fontSize: "20px" }}>
                  Item Details
                </p>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <LoadingSpinner />
              ) : error ? (
                <div className="text-center py-4 text-danger">
                  <h5>Error: {error}</h5>
                </div>
              ) : (
                <div className="mt-3 px-4">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <MasterTableContainer
                      columns={columns}
                      data={data || []}
                      totalRecords={detailRowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={setPageIndex}
                     onPageSizeChange={(newSize: number) => {
    setPageSize(newSize); // update page size
    setPageIndex(0); // reset to first page
  }}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setFilterValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      manualSorting={true}
                      onTableReady={setTableInstance}
                    />
                  </LoaderErrorWrapper>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </Fragment>
  );
};

export default ProductStockInfo;
